import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

const CART_MUTATION_MARKERS = Object.freeze({
  quantity: 'data-cart-update-busy',
  benefits: 'data-benefits-busy',
  upsell: 'data-upsell-busy',
  discount: 'data-discount-busy',
});

const CART_MUTABLE_CONTROL_SELECTOR = [
  'cart-quantity-selector-component button',
  'cart-quantity-selector-component input',
  '.cart-items__remove',
  '[data-gift-choose]',
  '[data-cart-gift-wrap] input',
  '[data-upsell-add]',
  '[data-upsell-retry]',
  '.cart-discount__button',
  '.cart-discount__pill-remove',
].join(',');

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce((event) => {
    const pending = this.#pendingQuantityChange;
    this.#pendingQuantityChange = null;
    if (!pending) return;

    const currentKey = this.#getCartItemRow(pending.line)?.dataset.key || '';
    if (pending.revision !== this.#cartRevision || (pending.key && currentKey !== pending.key)) return;
    this.#onQuantityChange(event);
  }, 120);

  /** @type {{ line: number, key: string, revision: number } | null} */
  #pendingQuantityChange = null;

  #cartRevision = 0;

  /** @type {Set<'quantity' | 'benefits' | 'upsell' | 'discount'>} */
  #mutationOwners = new Set();

  #cartWasBusy = false;

  #queueQuantityChange = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    const line = Number(event.detail?.cartLine || 0);
    if (!line) return;
    if (this.isMutationBusy()) {
      this.#restoreQuantitySelector(line);
      return;
    }

    if (this.#pendingQuantityChange && this.#pendingQuantityChange.line !== line) {
      this.#restoreQuantitySelector(this.#pendingQuantityChange.line);
    }

    this.#pendingQuantityChange = {
      line,
      key: this.#getCartItemRow(line)?.dataset.key || '',
      revision: this.#cartRevision,
    };
    this.#debouncedOnChange(event);
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#queueQuantityChange);
    for (const [source, marker] of Object.entries(CART_MUTATION_MARKERS)) {
      if (this.hasAttribute(marker)) this.#mutationOwners.add(source);
    }
    this.#syncCartBusyState();
  }

  updatedCallback() {
    super.updatedCallback();
    this.refreshMutationBusyState();
  }

  /** Reapplies the durable lock to server-rendered controls after a section morph. */
  refreshMutationBusyState() {
    this.#cartRevision += 1;
    this.#cancelPendingQuantityChange(false);
    this.#syncCartBusyState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#queueQuantityChange);
    this.#cancelPendingQuantityChange(false);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (this.isMutationBusy()) {
      this.#restoreQuantitySelector(line);
      return;
    }

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    const started = this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    if (!started) {
      this.#restoreQuantitySelector(line);
      return;
    }
    const lineItemRow = this.#getCartItemRow(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    if (this.isMutationBusy()) return;

    const started = this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });
    if (!started) return;

    const cartItemRowToRemove = this.#getCartItemRow(line);

    if (!cartItemRowToRemove) return;

    const cartItemRows = this.#getCartItemRows();
    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = cartItemRows.length > 0 && rowsToRemove.length === cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @returns {boolean} Whether the mutation was started.
   */
  updateQuantity(config) {
    if (this.isMutationBusy()) return false;

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });

        this.#updateCartQuantitySelectorButtonStates();
        // O morph cria novos controles enquanto a requisição ainda está
        // finalizando. Reaplica o lock também nesses novos nós.
        this.refreshMutationBusyState();
      })
      .catch((error) => {
        resetShimmer(this);
        this.#restoreQuantitySelector(line);
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });

    return true;
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    // If the section HTML is already in the event payload, cart-discount.js will call
    // morphSection synchronously right after dispatching this event — skip the extra fetch+morph.
    if (event.detail.resource?.sections?.[this.sectionId]) return;
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.#getQuantitySelector(line);
    const quantityInput = quantitySelector?.querySelector('input');

    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemRow = this.#getCartItemRow(line);
    const cartItemError =
      this.refs[`cartItemError-${line}`] ?? cartItemRow?.querySelector(`[ref="cartItemError-${line}"]`);
    const cartItemErrorContainer =
      this.refs[`cartItemErrorContainer-${line}`] ??
      cartItemRow?.querySelector(`[ref="cartItemErrorContainer-${line}"]`);

    if (!(cartItemError instanceof HTMLElement) || !(cartItemErrorContainer instanceof HTMLElement)) return;

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target !== this) this.#cancelPendingQuantityChange(true);

    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    // O engine de benefícios já morfa a própria seção antes de publicar o
    // evento. Repetir o morph aqui apaga a posição da gaveta e faz o conteúdo
    // saltar para o topo alguns instantes depois do clique na embalagem.
    if (event.detail.data?.source === 'cart-benefits-component') return;
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
      this.refreshMutationBusyState();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.setMutationBusy('quantity', true);
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.setMutationBusy('quantity', false);
  }

  /**
   * Reports whether another cart mutation is in flight.
   * @param {'quantity' | 'benefits' | 'upsell' | 'discount' | ''} [exceptSource]
   * @returns {boolean}
   */
  isMutationBusy(exceptSource = '') {
    return [...this.#mutationOwners].some((source) => source !== exceptSource);
  }

  /**
   * Central lock shared by quantity, benefits, upsell and discounts.
   * @param {'quantity' | 'benefits' | 'upsell' | 'discount'} source
   * @param {boolean} busy
   */
  setMutationBusy(source, busy) {
    const marker = CART_MUTATION_MARKERS[source];
    if (!marker) return;

    // Quando a alteração de quantidade nasce do debounce, o pendente já foi
    // consumido. Em qualquer outro início (inclusive remover linha), cancelar
    // evita que um timer antigo use índices reordenados.
    if (busy) this.#cancelPendingQuantityChange(true);
    if (busy) this.#mutationOwners.add(source);
    else this.#mutationOwners.delete(source);
    this.#syncCartBusyState();
  }

  /** Keeps independent cart mutations from unlocking each other's controls. */
  #syncCartBusyState() {
    const remainsBusy = this.isMutationBusy();

    // Section morphs copy server attributes over the live custom element.
    // Rebuild every marker from the durable owner set before touching controls.
    for (const [source, marker] of Object.entries(CART_MUTATION_MARKERS)) {
      this.toggleAttribute(marker, this.#mutationOwners.has(source));
    }

    this.classList.toggle('cart-items-disabled', remainsBusy);
    if (remainsBusy) this.setAttribute('aria-busy', 'true');
    else this.removeAttribute('aria-busy');

    for (const control of this.querySelectorAll(CART_MUTABLE_CONTROL_SELECTOR)) {
      if (!(control instanceof HTMLButtonElement || control instanceof HTMLInputElement)) continue;

      if (remainsBusy && !control.disabled) {
        control.disabled = true;
        control.setAttribute('data-cart-mutation-disabled', '');
      } else if (!remainsBusy && control.hasAttribute('data-cart-mutation-disabled')) {
        control.disabled = false;
        control.removeAttribute('data-cart-mutation-disabled');
      }
    }

    for (const button of this.querySelectorAll('button[name="checkout"]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      if (remainsBusy && !button.disabled) {
        button.disabled = true;
        button.setAttribute('data-cart-busy-disabled', '');
      } else if (
        !remainsBusy &&
        button.hasAttribute('data-cart-busy-disabled') &&
        !button.hasAttribute('data-benefits-reconciliation')
      ) {
        button.disabled = false;
        button.removeAttribute('data-cart-busy-disabled');
      }
    }

    for (const accelerated of this.querySelectorAll('.additional-checkout-buttons')) {
      if (!(accelerated instanceof HTMLElement)) continue;
      if (remainsBusy && !accelerated.hasAttribute('inert')) {
        accelerated.setAttribute('inert', '');
        accelerated.setAttribute('data-cart-busy-inert', '');
      } else if (!remainsBusy && accelerated.hasAttribute('data-cart-busy-inert')) {
        accelerated.removeAttribute('inert');
        accelerated.removeAttribute('data-cart-busy-inert');
      }
    }

    const wasBusy = this.#cartWasBusy;
    this.#cartWasBusy = remainsBusy;
    if (wasBusy && !remainsBusy) {
      this.dispatchEvent(new CustomEvent('cart:mutation-idle', { bubbles: true }));
    }
  }

  /** Restores an optimistically edited quantity when another request owns the cart lock. */
  #restoreQuantitySelector(line) {
    const selector = this.#getQuantitySelector(line);
    const input = selector?.querySelector('input');
    if (input instanceof HTMLInputElement) input.value = input.defaultValue;
    /** @type {any} */ (selector)?.updateButtonStates?.();
  }

  /** Cancels a debounced quantity edit before its Shopify line number becomes stale. */
  #cancelPendingQuantityChange(restore) {
    this.#debouncedOnChange.cancel();
    if (restore && this.#pendingQuantityChange) {
      this.#restoreQuantitySelector(this.#pendingQuantityChange.line);
    }
    this.#pendingQuantityChange = null;
  }

  /**
   * Gets the current cart rows after section morphs.
   * @returns {HTMLTableRowElement[]}
   */
  #getCartItemRows() {
    const rows = this.refs.cartItemRows;
    if (Array.isArray(rows)) return rows.filter((row) => row instanceof HTMLTableRowElement);
    return rows instanceof HTMLTableRowElement ? [rows] : [];
  }

  /**
   * Finds a cart row by its real Shopify line number.
   * @param {number} line
   * @returns {HTMLTableRowElement | null}
   */
  #getCartItemRow(line) {
    const namedRow = this.refs[`cartItemRow-${line}`];
    if (namedRow instanceof HTMLTableRowElement) return namedRow;

    const rows = this.#getCartItemRows();
    const rowByLine = rows.find((row) => Number(row.dataset.cartLine) === line);
    if (rowByLine) return rowByLine;

    const queriedRow = this.querySelector(`.cart-items__table-row[data-cart-line="${line}"]`);
    if (queriedRow instanceof HTMLTableRowElement) return queriedRow;

    const hasLineMetadata = rows.some((row) => row.dataset.cartLine !== undefined);
    return hasLineMetadata ? null : rows[line - 1] ?? null;
  }

  /**
   * Gets the current quantity selectors after section morphs.
   * @returns {HTMLElement[]}
   */
  #getQuantitySelectors() {
    const selectors = this.refs.quantitySelectors;
    if (Array.isArray(selectors)) return selectors.filter((selector) => selector instanceof HTMLElement);
    return selectors instanceof HTMLElement ? [selectors] : [];
  }

  /**
   * Finds a quantity selector by the line metadata on its input.
   * @param {number} line
   * @returns {HTMLElement | null}
   */
  #getQuantitySelector(line) {
    const namedSelector = this.refs[`quantitySelector-${line}`];
    if (namedSelector instanceof HTMLElement) return namedSelector;

    const selectors = this.#getQuantitySelectors();
    const selectorByLine = selectors.find(
      (selector) => Number(selector.querySelector('input[data-cart-line]')?.dataset.cartLine) === line
    );
    if (selectorByLine) return selectorByLine;

    const inputByLine = this.querySelector(`input[data-cart-line="${line}"]`);
    const queriedSelector = inputByLine?.closest('cart-quantity-selector-component');
    if (queriedSelector instanceof HTMLElement) return queriedSelector;

    const hasLineMetadata = selectors.some((selector) => selector.querySelector('input[data-cart-line]'));
    return hasLineMetadata ? null : selectors[line - 1] ?? null;
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    // A mesma variante pode existir em linhas separadas por properties
    // (por exemplo, uma compra normal e um brinde). O limite de estoque deve
    // receber a soma real da variante, não a quantidade da última linha.
    const quantitiesByVariant = new Map();
    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      quantitiesByVariant.set(variantId, (quantitiesByVariant.get(variantId) || 0) + item.quantity);
    }

    for (const [variantId, quantity] of quantitiesByVariant) {
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
